// client/src/pages/AdminPage.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { TranslationsEditor, CategoryTranslationsEditor } from '../components/menu/TranslationsEditor';
import QRTab from '../components/qr/QRTab';
import QRModal from '../components/QRModal';
import FeatureSettings from '../components/admin/FeatureSettings';
import ImageUploadHint from '../components/ui/ImageUploadHint';
import OrdersTab from '../components/orders/OrdersTab';
import StaffTab from '../components/staff/StaffTab';
import DeliveryTab from '../components/admin/DeliveryTab';
import {
  formatRestaurantPrice,
  normalizeCurrency,
  MARKET_SEGMENTS,
} from '../utils/formatPrice';
import BrandFooter from '../components/BrandFooter';
import { useStaffWebPush } from '../hooks/useStaffWebPush';

export default function AdminPage({ token, user, onLogout }) {
  const [restaurantSlug, setRestaurantSlug] = useState(null);
  const [items, setItems]           = useState([]);
  const [categories, setCategories] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [activeTab, setActiveTab]   = useState('items');
  const [editItem, setEditItem]     = useState(null);
  const [loading, setLoading]       = useState(false);
  const [topBanner, setTopBanner] = useState({ text: '', kind: 'success' });
  const [showQR, setShowQR] = useState(false);
  const topBannerTimerRef = useRef(null);
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;

  // Токен на каждый запрос из sessionStorage — надёжнее при ре-логине и обходит прокси/кэш дефолтных headers
  const api = useMemo(() => {
    const inst = axios.create();
    inst.interceptors.request.use((config) => {
      const t =
        typeof sessionStorage !== 'undefined'
          ? localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')
          : null;
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });
    inst.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 401) {
          const serverMsg = err.response?.data?.error || 'Сессия истекла';
          try {
            sessionStorage.setItem('auth_flash', `❌ ${serverMsg}. Войдите снова.`);
          } catch (_) { /* no-op */ }
          onLogoutRef.current?.();
          const path = window.location?.pathname || '';
          if (path !== '/login' && path !== '/waiter-login') {
            window.location.replace('/login');
          }
        }
        return Promise.reject(err);
      }
    );
    return inst;
  }, []);

  const pushRestaurantId = restaurant?.id || user?.restaurant_id;
  useStaffWebPush({
    enabled:
      !!pushRestaurantId && ['waiter', 'owner', 'superadmin'].includes(user?.role),
    api,
    restaurantId: pushRestaurantId,
    userRole: user?.role,
  });

  // Получаем slug ресторана по restaurant_id из токена (тот же api → единая обработка 401)
  useEffect(() => {
    if (!token || !user) return;
    if (user.role === 'superadmin' && !user.restaurant_id) {
      window.location.href = '/superadmin';
      return;
    }
    if (!user.restaurant_id) return;

    api
      .get('/api/auth/my-restaurant')
      .then(({ data }) => {
        if (data?.slug) setRestaurantSlug(data.slug);
      })
      .catch((err) => {
        console.error('my-restaurant:', err?.response?.status, err?.message);
      });
  }, [user, token, api]);

  useEffect(() => { if (restaurantSlug) fetchAll(); }, [token, restaurantSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user?.role === 'waiter' && activeTab !== 'orders') setActiveTab('orders');
  }, [user?.role, activeTab]);

  const fetchAll = async () => {
    try {
      const { data } = await api.get(`/api/menu/${restaurantSlug}`);
      setRestaurant(data.restaurant);
      setCategories(data.categories || []);
      const flat = (data.categories || []).flatMap((cat) =>
        (cat.items || []).map((i) => ({
          ...i,
          category_name: cat.name_ru || cat.name,
        }))
      );
      setItems(flat);
    } catch (err) { console.error(err); }
  };

  const showMessage = useCallback((text) => {
    const t = String(text);
    const kind =
      t.trimStart().startsWith('❌') ? 'error' :
        t.includes('⚠️') ? 'warning' :
          t.includes('⏳') ? 'info' :
            'success';
    if (topBannerTimerRef.current) clearTimeout(topBannerTimerRef.current);
    setTopBanner({ text, kind });
    const ms = kind === 'error' ? 10000 : kind === 'info' ? 25000 : kind === 'warning' ? 6000 : 4500;
    topBannerTimerRef.current = setTimeout(() => {
      setTopBanner({ text: '', kind: 'success' });
    }, ms);
  }, []);

  useEffect(() => () => {
    if (topBannerTimerRef.current) clearTimeout(topBannerTimerRef.current);
  }, []);

  const handleItemImageUpload = async (itemId, file) => {
    if (!file) return;
    const sizeMB = file.size / 1024 / 1024;
    if (sizeMB > 20) {
      showMessage('❌ Файл слишком большой (макс. 20MB)');
      return;
    }
    if (sizeMB > 5) {
      showMessage(`⚠️ Большой файл (${sizeMB.toFixed(1)}MB) — сжимаем автоматически`);
    }
    const formData = new FormData();
    formData.append('image', file);
    setLoading(true);
    try {
      showMessage('⏳ Загружаем и оптимизируем...');
      await api.post(`/api/admin/items/${itemId}/image`, formData);
      showMessage('✅ Фото загружено!');
      fetchAll();
    } catch { showMessage('❌ Ошибка загрузки фото'); }
    finally { setLoading(false); }
  };

  const handleCoverUpload = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    setLoading(true);
    try {
      await api.post(`/api/admin/restaurant/${restaurantSlug}/cover`, formData);
      showMessage('✅ Обложка обновлена!');
      fetchAll();
    } catch { showMessage('❌ Ошибка загрузки обложки'); }
    finally  { setLoading(false); }
  };

  const handleSaveItem = async (item) => {
    try {
      await api.put(`/api/admin/items/${item.id}`, item);
      showMessage('✅ Блюдо сохранено!');
      setEditItem(null);
      fetchAll();
    } catch { showMessage('❌ Ошибка сохранения'); }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Удалить блюдо?')) return;
    try {
      await api.delete(`/api/admin/items/${id}`);
      showMessage('✅ Блюдо удалено');
      fetchAll();
    } catch { showMessage('❌ Ошибка удаления'); }
  };

  const showTopBanner = !!topBanner.text || loading;
  return (
    <div
      style={{
        ...styles.page,
        paddingTop: showTopBanner ? 60 : 16,
      }}
    >
      {showTopBanner && (
        <div
          style={styles.topBannerHost}
          role="status"
          aria-live="polite"
        >
          <div
            style={{
              ...styles.topBannerCard,
              ...bannerColor(topBanner.kind, !!topBanner.text),
            }}
          >
            {loading && (
              <div style={{ marginBottom: topBanner.text ? 6 : 0, fontSize: 13, opacity: 0.95 }}>
                ⏳ Загрузка...
              </div>
            )}
            {!!topBanner.text && <div style={{ textAlign: 'center' }}>{topBanner.text}</div>}
          </div>
        </div>
      )}
      {user?.role === 'waiter' ? (
        <div
          style={{
            backgroundColor: '#111',
            color: '#fff',
            margin: '-16px -12px 12px -12px',
            padding: 'max(10px, env(safe-area-inset-top, 0px)) 16px 14px 16px',
            borderRadius: '0 0 16px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '18px', fontWeight: '800', lineHeight: 1.2 }}>🤵 Панель официанта</div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)', marginTop: '4px', wordBreak: 'break-all' }}>
              {user?.email}
            </div>
            {(restaurantSlug || restaurant?.slug) && (
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
                slug: {restaurantSlug || restaurant?.slug}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onLogout}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: 'rgba(255,255,255,0.12)',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              flexShrink: 0,
            }}
          >
            Выйти
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <h1 style={{ ...styles.title, marginBottom: 0, lineHeight: 1.2 }}>
            <span>🍽️ Меню</span>
            {(restaurantSlug || restaurant?.slug) && (
              <span style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '600',
                color: '#888',
                marginTop: '4px',
                letterSpacing: '0.02em',
              }}>
                slug: <code style={{ background: '#eee', padding: '2px 8px', borderRadius: '6px' }}>{restaurantSlug || restaurant?.slug}</code>
              </span>
            )}
          </h1>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '13px', color: '#888' }}>{user?.email}</div>
            <button
              onClick={onLogout}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#FFEBEE',
                color: '#E85D4A',
                cursor: 'pointer',
                fontSize: '13px',
                marginTop: '4px',
              }}
            >
              Выйти
            </button>
            {restaurant && (
              <button
                onClick={() => setShowQR(true)}
                style={{
                  padding: '6px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#F3E5F5',
                  color: '#7B1FA2',
                  cursor: 'pointer',
                  fontSize: '13px',
                  marginTop: '4px',
                  marginLeft: '6px',
                }}
              >
                QR-код
              </button>
            )}
          </div>
        </div>
      )}

      {/* Табы */}
      <div style={styles.tabs}>
        {[
          ...(user?.role !== 'waiter' ? [{ key:'items', label:'Меню', icon:'🍕' }] : []),
          ...(user?.role !== 'waiter' ? [{ key:'qr', label:'QR-коды', icon:'📱' }] : []),
          ...(user?.role !== 'waiter' ? [{ key:'features', label:'Настройки', icon:'⚙️' }] : []),
          ...(user?.role !== 'waiter' ? [{ key:'restaurant', label:'Ресторан', icon:'🏠' }] : []),
          ...(user?.role !== 'waiter' ? [{ key:'staff', label:'Персонал', icon:'👤' }] : []),
          { key:'orders',     label:'Заказы',    icon:'📋' },
          ...(user?.role !== 'waiter' ? [{ key:'delivery', label:'Доставка', icon:'🚚' }] : []),
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              ...styles.tab,
              ...(activeTab === tab.key ? styles.tabActive : {}),
            }}
          >
            <span style={{ fontSize:'20px' }}>{tab.icon}</span>
            <span style={{ fontSize:'15px', fontWeight:'500' }}>{tab.label}</span>
            <span style={{
              marginLeft:'auto',
              fontSize:'18px',
              color: activeTab === tab.key ? '#fff' : '#C7C7CC',
            }}>›</span>
          </button>
        ))}
      </div>

      {/* ── Меню (категории + блюда) ── */}
      {activeTab === 'qr' && (
        <QRTab restaurant={restaurant} />
      )}

      {activeTab === 'features' && (
        <FeatureSettings restaurant={restaurant} api={api} onImportSuccess={fetchAll} />
      )}

      {activeTab === 'items' && (
        <MenuTab
          categories={categories}
          items={items}
          restaurant={restaurant}
          menuCurrency={normalizeCurrency(restaurant?.currency)}
          api={api}
          editItem={editItem}
          setEditItem={setEditItem}
          onSaveItem={handleSaveItem}
          onDeleteItem={handleDeleteItem}
          onImageUpload={handleItemImageUpload}
          onRefresh={fetchAll}
          showMessage={showMessage}
        />
      )}

      {/* ── Персонал ── */}
      {activeTab === 'staff' && user?.role !== 'waiter' && (restaurant?.id || user?.restaurant_id) && (
        <StaffTab
          api={api}
          restaurantId={restaurant?.id || user?.restaurant_id}
          restaurant={restaurant}
        />
      )}

      {/* ── Доставка ── */}
      {activeTab === 'delivery' && restaurant && (
        <DeliveryTab
          api={api}
          restaurant={restaurant}
        />
      )}

      {/* ── Заказы ── */}
      {activeTab === 'orders' && (restaurant?.id || user?.restaurant_id) && (
        <OrdersTab
          api={api}
          restaurantId={restaurant?.id || user?.restaurant_id}
          userRole={user?.role}
          menuItems={items}
          categories={categories}
          menuCurrency={normalizeCurrency(restaurant?.currency)}
        />
      )}

      {/* ── Ресторан ── */}
      {activeTab === 'restaurant' && restaurant && (
        <RestaurantForm
          restaurant={restaurant}
          onCoverUpload={handleCoverUpload}
          onSave={async (data) => {
            await api.put(`/api/admin/restaurant/${restaurantSlug}`, data);
            fetchAll();
          }}
        />
      )}

      {showQR && restaurant && (
        <QRModal
          restaurant={restaurant}
          onClose={() => setShowQR(false)}
        />
      )}

      <BrandFooter />
    </div>
  );
}

// ─── MenuTab: категории + блюда вместе ─────────────────────────
function MenuTab({
  categories, items, restaurant, menuCurrency = 'UZS', api,
  editItem, setEditItem, onSaveItem, onDeleteItem,
  onImageUpload, onRefresh, showMessage,
}) {
  const [showAddItem, setShowAddItem] = useState(false);
  const [addToCatId, setAddToCatId] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [editCat, setEditCat] = useState(null);
  const [editCatData, setEditCatData] = useState({});
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');

  const uncategorized = items.filter((i) => !i.category_id);

  const orderedCategories = useMemo(
    () =>
      [...categories].sort((a, b) => {
        const da = Number(a.sort_order) || 0;
        const db = Number(b.sort_order) || 0;
        if (da !== db) return da - db;
        return String(a.name_ru || a.name || '').localeCompare(
          String(b.name_ru || b.name || ''),
          'ru'
        );
      }),
    [categories]
  );

  const moveCategory = async (catId, direction) => {
    const idx = orderedCategories.findIndex((c) => c.id === catId);
    if (idx < 0) return;
    const j = direction === 'up' ? idx - 1 : idx + 1;
    if (j < 0 || j >= orderedCategories.length) return;
    const next = [...orderedCategories];
    [next[idx], next[j]] = [next[j], next[idx]];
    const ids = next.map((c) => c.id);
    try {
      await api.patch('/api/admin/categories/reorder', { ids });
      onRefresh();
      showMessage('✅ Порядок категорий сохранён');
    } catch (err) {
      showMessage(`❌ ${err?.response?.data?.error || 'Ошибка порядка'}`);
    }
  };

  const toggleCollapse = (catId) =>
    setCollapsed((prev) => ({ ...prev, [catId]: !prev[catId] }));

  const handleAddCat = async () => {
    if (!newCatName.trim()) return;
    try {
      await api.post('/api/admin/categories', {
        restaurant_id: restaurant.id,
        name_ru: newCatName.trim(),
        name: newCatName.trim(),
      });
      setNewCatName('');
      setShowAddCat(false);
      onRefresh();
      showMessage('✅ Категория добавлена');
    } catch {
      showMessage('❌ Ошибка');
    }
  };

  const handleSaveCat = async (cat) => {
    if (!editCatData?.name_ru?.trim()) return;
    try {
      await api.put(`/api/admin/categories/${cat.id}`, {
        name_ru: editCatData.name_ru.trim(),
        name_uz: editCatData.name_uz || null,
        name_en: editCatData.name_en || null,
        name: editCatData.name_ru.trim(),
      });
      setEditCat(null);
      onRefresh();
      showMessage('✅ Сохранено');
    } catch {
      showMessage('❌ Ошибка');
    }
  };

  const handleDeleteCat = async (cat) => {
    const catItems = items.filter((i) => i.category_id === cat.id);
    if (catItems.length > 0) {
      showMessage(`❌ Нельзя удалить — в категории ${catItems.length} блюд`);
      return;
    }
    if (!window.confirm(`Удалить категорию «${cat.name_ru || cat.name}»?`)) return;
    try {
      await api.delete(`/api/admin/categories/${cat.id}`);
      onRefresh();
      showMessage('✅ Категория удалена');
    } catch {
      showMessage('❌ Ошибка');
    }
  };

  const handleCategoryImageUpload = async (catId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      await api.post(`/api/admin/categories/${catId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showMessage('✅ Фото категории загружено');
      onRefresh();
    } catch (err) {
      showMessage('❌ ' + (err.response?.data?.error || 'Ошибка загрузки'));
    }
  };

  const renderItem = (item) => (
    <div key={item.id} style={mt.itemCard}>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          {item.image_url ? (
            <img
              src={item.image_url}
              alt={item.name_ru || item.name}
              style={{
                width: '64px',
                height: '64px',
                objectFit: 'cover',
                borderRadius: '10px',
              }}
            />
          ) : (
            <div style={mt.imgPlaceholder}>🍽️</div>
          )}
          <label style={mt.uploadBtn} title="JPG, PNG, WebP, HEIC. Автосжатие до 800×800 WebP.">
            📷
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => onImageUpload(item.id, e.target.files[0])}
            />
          </label>
        </div>

        {editItem?.id === item.id ? (
          <EditItemForm
            item={editItem}
            categories={categories}
            onChange={setEditItem}
            onSave={() => onSaveItem(editItem)}
            onCancel={() => setEditItem(null)}
            enabledLanguages={restaurant?.enabled_languages}
            priceCurrency={menuCurrency}
          />
        ) : (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={mt.itemName}>{item.name_ru || item.name}</div>
            <div style={mt.itemPrice}>{formatRestaurantPrice(item.price, menuCurrency)}</div>
            <div style={mt.itemMeta}>
              {item.weight && `${item.weight} · `}
              <span style={{ color: item.is_available ? '#4CAF50' : '#E85D4A' }}>
                {item.is_available ? '✅ Доступно' : '❌ Скрыто'}
              </span>
              <span style={{ marginLeft: '8px', fontSize: '11px', color: '#bbb' }}>
                RU{item.name_ru ? '●' : '○'} UZ{item.name_uz ? '●' : '○'} EN{item.name_en ? '●' : '○'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button onClick={() => setEditItem({ ...item })} style={mt.btnEdit}>
                Изменить
              </button>
              <button onClick={() => onDeleteItem(item.id)} style={mt.btnDelete}>
                Удалить
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderCategory = (cat, orderIndex) => {
    const catItems = items.filter((i) => i.category_id === cat.id);
    const isCollapsed = collapsed[cat.id];
    const canUp = orderIndex > 0;
    const canDown = orderIndex < orderedCategories.length - 1;

    return (
      <div key={cat.id} style={mt.catBlock}>
        <div style={mt.catHeader}>
          <div style={mt.reorderWrap} title="Порядок в меню">
            <button
              type="button"
              style={{ ...mt.reorderBtn, opacity: canUp ? 1 : 0.25 }}
              disabled={!canUp}
              onClick={() => moveCategory(cat.id, 'up')}
              aria-label="Выше"
            >
              ↑
            </button>
            <button
              type="button"
              style={{ ...mt.reorderBtn, opacity: canDown ? 1 : 0.25 }}
              disabled={!canDown}
              onClick={() => moveCategory(cat.id, 'down')}
              aria-label="Ниже"
            >
              ↓
            </button>
          </div>
          <button style={mt.collapseBtn} onClick={() => toggleCollapse(cat.id)}>
            {isCollapsed ? '▶' : '▼'}
          </button>

          <label
            style={mt.catImageWrap}
            title="Нажмите, чтобы загрузить фото категории"
          >
            {cat.image_url ? (
              <img
                src={cat.image_url}
                alt=""
                style={mt.catImageThumb}
              />
            ) : (
              <div style={mt.catImagePlaceholder}>🍽️</div>
            )}
            <span style={mt.catImageCam}>📷</span>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                handleCategoryImageUpload(cat.id, e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>

          {editCat === cat.id ? (
            <div style={{ flex: 1, minWidth: 0 }}>
              <CategoryTranslationsEditor
                cat={editCatData}
                onChange={setEditCatData}
                enabledLanguages={restaurant?.enabled_languages}
              />
            </div>
          ) : (
            <div style={mt.catName}>
              {cat.name_ru || cat.name}
              <span style={{ marginLeft: '6px', fontSize: '11px', color: '#bbb' }}>
                RU{cat.name_ru ? '●' : '○'} UZ{cat.name_uz ? '●' : '○'} EN{cat.name_en ? '●' : '○'}
              </span>
              <span style={mt.catCount}>{catItems.length}</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
            {editCat === cat.id ? (
              <>
                <button style={mt.btnSaveCat} onClick={() => handleSaveCat(cat)}>
                  Сохранить
                </button>
                <button style={mt.btnCancelCat} onClick={() => setEditCat(null)}>
                  Отмена
                </button>
              </>
            ) : (
              <>
                <button
                  style={mt.btnAddInCat}
                  onClick={() => {
                    setAddToCatId(cat.id);
                    setShowAddItem(true);
                  }}
                >
                  + Блюдо
                </button>
                <button
                  style={mt.btnEditCat}
                  onClick={() => {
                    setEditCat(cat.id);
                    setEditCatData({
                      name_ru: cat.name_ru || cat.name || '',
                      name_uz: cat.name_uz || '',
                      name_en: cat.name_en || '',
                    });
                  }}
                >
                  ✏️
                </button>
                <button style={mt.btnDeleteCat} onClick={() => handleDeleteCat(cat)}>
                  🗑️
                </button>
              </>
            )}
          </div>
        </div>

        {showAddItem && addToCatId === cat.id && (
          <div style={{ padding: '0 0 8px 0' }}>
            <AddItemForm
              categories={categories}
              defaultCategoryId={cat.id}
              priceCurrency={menuCurrency}
              onSave={async (data) => {
                try {
                  await api.post('/api/admin/items', {
                    ...data,
                    restaurant_id: restaurant.id,
                    category_id: cat.id,
                  });
                  setShowAddItem(false);
                  setAddToCatId(null);
                  onRefresh();
                  showMessage('✅ Блюдо добавлено');
                } catch {
                  showMessage('❌ Ошибка');
                }
              }}
              onCancel={() => {
                setShowAddItem(false);
                setAddToCatId(null);
              }}
            />
          </div>
        )}

        {!isCollapsed && (
          <div style={mt.itemsList}>
            {catItems.length === 0 ? (
              <div style={mt.emptyMsg}>Нет блюд — нажмите «+ Блюдо»</div>
            ) : (
              catItems.map(renderItem)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <ImageUploadHint style={{ marginBottom: '12px' }} />
      <div style={{
        marginBottom: '12px',
        padding: '10px 14px',
        borderRadius: '12px',
        backgroundColor: menuCurrency === 'RUB' ? '#E8EAF6' : '#FFF8E1',
        border: '1px solid',
        borderColor: menuCurrency === 'RUB' ? '#C5CAE9' : '#FFE082',
        fontSize: '13px',
        lineHeight: 1.45,
        color: '#333',
      }}>
        <strong>Валюта цен в меню сейчас:</strong>{' '}
        {menuCurrency === 'RUB' ? 'рубли (₽)' : 'сумы (UZS)'}
        {menuCurrency !== 'RUB' && (
          <>
            {' '}— чтобы вводить цены в рублях, открой вкладку <strong>«Ресторан»</strong>,
            выбери <strong>«Валюта цен в меню» → RUB</strong> (или сегмент <strong>«Россия»</strong>)
            и нажми <strong>«Сохранить»</strong>, затем вернись сюда.
          </>
        )}
      </div>
      <p style={{ fontSize: '12px', color: '#888', margin: '0 0 10px' }}>
        У категории слева от названия — иконка 📷: нажмите на превью, чтобы сменить фото категории.
      </p>
      {!showAddCat && (
        <button style={mt.btnAddCat} onClick={() => setShowAddCat(true)}>
          + Добавить категорию
        </button>
      )}

      {showAddCat && (
        <div style={mt.addCatForm}>
          <input
            style={mt.catInput}
            placeholder="Название категории..."
            value={newCatName}
            autoFocus
            onChange={(e) => setNewCatName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddCat();
              if (e.key === 'Escape') setShowAddCat(false);
            }}
          />
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button style={mt.btnSaveCat} onClick={handleAddCat}>
              Создать
            </button>
            <button style={mt.btnCancelCat} onClick={() => setShowAddCat(false)}>
              Отмена
            </button>
          </div>
        </div>
      )}

      {categories.length === 0 && (
        <div style={mt.emptyMsg}>Нет категорий — создайте первую!</div>
      )}
      {orderedCategories.map((cat, i) => renderCategory(cat, i))}

      {uncategorized.length > 0 && (
        <div style={mt.catBlock}>
          <div style={mt.catHeader}>
            <div style={{ ...mt.catName, color: '#999' }}>
              Без категории
              <span style={mt.catCount}>{uncategorized.length}</span>
            </div>
          </div>
          <div style={mt.itemsList}>{uncategorized.map(renderItem)}</div>
        </div>
      )}
    </div>
  );
}

const mt = {
  catBlock: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    marginBottom: '10px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  catHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '14px 16px',
    backgroundColor: '#f9f9f9',
    borderBottom: '1px solid #f0f0f0',
  },
  catName: {
    fontSize: '15px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  catCount: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#fff',
    backgroundColor: '#C7C7CC',
    borderRadius: '100px',
    padding: '2px 8px',
    minWidth: '20px',
    textAlign: 'center',
  },
  catInput: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: '10px',
    border: '1px solid #E0E0E0',
    fontSize: '14px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  reorderWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flexShrink: 0,
  },
  reorderBtn: {
    width: '28px',
    height: '22px',
    padding: 0,
    border: '1px solid #ddd',
    borderRadius: '6px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: '12px',
    lineHeight: 1,
    color: '#333',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  collapseBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#999',
    padding: '4px',
    flexShrink: 0,
  },
  catImageWrap: {
    position: 'relative',
    flexShrink: 0,
    cursor: 'pointer',
    display: 'block',
  },
  catImageThumb: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    objectFit: 'cover',
    display: 'block',
    backgroundColor: '#eee',
  },
  catImagePlaceholder: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    backgroundColor: '#EEEEEE',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
  },
  catImageCam: {
    position: 'absolute',
    bottom: '-2px',
    right: '-2px',
    width: '18px',
    height: '18px',
    backgroundColor: '#000',
    borderRadius: '50%',
    fontSize: '9px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    pointerEvents: 'none',
  },
  itemsList: { padding: '8px' },
  itemCard: { padding: '12px', borderBottom: '1px solid #f5f5f5' },
  imgPlaceholder: {
    width: '64px',
    height: '64px',
    borderRadius: '10px',
    backgroundColor: '#f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },
  uploadBtn: {
    position: 'absolute',
    bottom: '-4px',
    right: '-4px',
    backgroundColor: '#fff',
    borderRadius: '50%',
    width: '22px',
    height: '22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    cursor: 'pointer',
    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
    border: 'none',
  },
  itemName: { fontSize: '15px', fontWeight: '600', marginBottom: '2px' },
  itemPrice: {
    fontSize: '14px',
    color: '#E85D4A',
    fontWeight: '600',
    marginBottom: '2px',
  },
  itemMeta: { fontSize: '12px', color: '#999' },
  emptyMsg: {
    padding: '16px',
    textAlign: 'center',
    color: '#bbb',
    fontSize: '13px',
  },
  btnAddCat: {
    width: '100%',
    padding: '14px',
    borderRadius: '14px',
    border: '2px dashed #ddd',
    backgroundColor: 'transparent',
    fontSize: '14px',
    fontWeight: '600',
    color: '#666',
    cursor: 'pointer',
    marginBottom: '12px',
  },
  addCatForm: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '16px',
    marginBottom: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  btnAddInCat: {
    padding: '5px 10px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    cursor: 'pointer',
    fontSize: '12px',
    whiteSpace: 'nowrap',
  },
  btnEditCat: {
    padding: '5px 8px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#F0F0F0',
    cursor: 'pointer',
    fontSize: '13px',
  },
  btnDeleteCat: {
    padding: '5px 8px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#FFEBEE',
    cursor: 'pointer',
    fontSize: '13px',
  },
  btnSaveCat: {
    padding: '8px 16px',
    borderRadius: '10px',
    border: 'none',
    backgroundColor: '#000',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
  },
  btnCancelCat: {
    padding: '8px 16px',
    borderRadius: '10px',
    border: 'none',
    backgroundColor: '#F0F0F0',
    cursor: 'pointer',
    fontSize: '13px',
  },
  btnEdit: {
    padding: '6px 12px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#F0F0F0',
    cursor: 'pointer',
    fontSize: '12px',
  },
  btnDelete: {
    padding: '6px 12px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#FFEBEE',
    color: '#E85D4A',
    cursor: 'pointer',
    fontSize: '12px',
  },
};

// ─── Категории (legacy, не используется — заменён MenuTab) ─────
// eslint-disable-next-line no-unused-vars
function CategoriesTab({ categories, restaurantId, api, onRefresh, showMessage }) {
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [newName, setNewName]     = useState('');
  const [showAdd, setShowAdd]     = useState(false);
  const [list, setList]           = useState([]);
  const [dragging, setDragging]   = useState(null);

  const loadCategories = async () => {
    if (!restaurantId) return;
    try {
      const { data } = await api.get(`/api/admin/categories/${restaurantId}`);
      setList(data);
    } catch {
      setList(categories);
    }
  };

  useEffect(() => { loadCategories(); }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => { loadCategories(); onRefresh(); };

  // ── Загрузка фото категории ────────────────────────────────
  const handleImageUpload = async (catId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      await api.post(`/api/admin/categories/${catId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showMessage('✅ Фото загружено');
      refresh();
    } catch (err) {
      showMessage('❌ ' + (err.response?.data?.error || 'Ошибка'));
    }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    try {
      await api.post('/api/admin/categories', {
        restaurant_id: restaurantId,
        name_ru: newName.trim(),
        name:    newName.trim(),
      });
      showMessage('✅ Категория добавлена');
      setNewName(''); setShowAdd(false); refresh();
    } catch (err) { showMessage('❌ ' + (err.response?.data?.error || 'Ошибка')); }
  };

  const handleSave = async (id) => {
    if (!editValue.trim()) return;
    try {
      await api.put(`/api/admin/categories/${id}`, {
        name_ru: editValue.trim(), name: editValue.trim(),
      });
      showMessage('✅ Сохранено');
      setEditingId(null); refresh();
    } catch (err) { showMessage('❌ ' + (err.response?.data?.error || 'Ошибка')); }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Удалить «${cat.name_ru || cat.name}»?`)) return;
    try {
      await api.delete(`/api/admin/categories/${cat.id}`);
      showMessage('✅ Удалено'); refresh();
    } catch (err) { showMessage('❌ ' + (err.response?.data?.error || 'Ошибка')); }
  };

  const handleDragStart = (i) => setDragging(i);
  const handleDragOver  = (e, i) => {
    e.preventDefault();
    if (dragging === null || dragging === i) return;
    const arr = [...list];
    const [moved] = arr.splice(dragging, 1);
    arr.splice(i, 0, moved);
    setDragging(i); setList(arr);
  };
  const handleDragEnd = async () => {
    setDragging(null);
    try {
      await api.patch('/api/admin/categories/reorder', { ids: list.map((c) => c.id) });
      showMessage('✅ Порядок сохранён'); onRefresh();
    } catch { showMessage('❌ Ошибка порядка'); }
  };

  return (
    <div>
      <div style={{ ...styles.card, flexDirection:'column', padding:0, overflow:'hidden' }}>
        {list.length === 0 && (
          <div style={{ padding:'24px', textAlign:'center', color:'#999' }}>
            Нет категорий — добавьте первую!
          </div>
        )}

        {list.map((cat, index) => (
          <div key={cat.id} draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            style={{
              display:'flex', alignItems:'center', gap:'12px',
              padding:'12px 16px', cursor:'grab',
              borderBottom: index < list.length-1 ? '1px solid #F0F0F0' : 'none',
              opacity: dragging === index ? 0.5 : 1,
              background:'#fff',
            }}
          >
            {/* Drag handle */}
            <span style={{ fontSize:'16px', color:'#C7C7CC', flexShrink:0 }}>☰</span>

            {/* ── Превью фото ── */}
            <label style={{ position:'relative', flexShrink:0, cursor:'pointer' }}>
              {cat.image_url ? (
                <img
                  src={cat.image_url}
                  alt={cat.name_ru || cat.name}
                  style={{ width:'52px', height:'52px', borderRadius:'10px', objectFit:'cover', display:'block' }}
                />
              ) : (
                <div style={{
                  width:'52px', height:'52px', borderRadius:'10px',
                  backgroundColor:'#F0F0F0',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'22px',
                }}>
                  🍽️
                </div>
              )}
              {/* Кнопка камеры */}
              <div style={{
                position:'absolute', bottom:'-4px', right:'-4px',
                width:'20px', height:'20px',
                backgroundColor:'#000', borderRadius:'50%',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:'10px',
              }}>
                📷
              </div>
              <input
                type="file"
                accept="image/*"
                style={{ display:'none' }}
                onChange={(e) => handleImageUpload(cat.id, e.target.files[0])}
              />
            </label>

            {/* ── Название ── */}
            {editingId === cat.id ? (
              <input
                style={{ ...styles.input, flex:1 }}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter')  handleSave(cat.id);
                  if (e.key === 'Escape') setEditingId(null);
                }}
                autoFocus
              />
            ) : (
              <div style={{ flex:1 }}>
                <div style={{ fontSize:'15px', fontWeight:'500' }}>
                  {cat.name_ru || cat.name}
                </div>
                <div style={{ fontSize:'12px', color:'#8E8E93', marginTop:'2px' }}>
                  {cat.items_count ?? 0} блюд
                </div>
              </div>
            )}

            {/* ── Кнопки ── */}
            <div style={{ display:'flex', gap:'6px', flexShrink:0 }}>
              {editingId === cat.id ? (
                <>
                  <button style={styles.btnSave}
                    onClick={() => handleSave(cat.id)}>💾</button>
                  <button style={styles.btnCancel}
                    onClick={() => setEditingId(null)}>✕</button>
                </>
              ) : (
                <>
                  <button style={styles.btnEdit} onClick={() => {
                    setEditingId(cat.id);
                    setEditValue(cat.name_ru || cat.name);
                  }}>✏️</button>
                  <button style={styles.btnDelete}
                    onClick={() => handleDelete(cat)}>🗑️</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Добавить категорию ── */}
      {showAdd ? (
        <div style={{ ...styles.card, flexDirection:'column', gap:'12px', marginTop:'8px' }}>
          <input style={styles.input} placeholder="Название категории" value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter')  handleAdd();
              if (e.key === 'Escape') setShowAdd(false);
            }} autoFocus />
          <div style={{ display:'flex', gap:'8px' }}>
            <button style={{ ...styles.btnSave, flex:1, padding:'12px' }}
              onClick={handleAdd}>✅ Добавить</button>
            <button style={styles.btnCancel}
              onClick={() => setShowAdd(false)}>Отмена</button>
          </div>
        </div>
      ) : (
        <button
          style={{ ...styles.btnSave, width:'100%', marginTop:'8px', padding:'16px' }}
          onClick={() => setShowAdd(true)}>
          + Добавить категорию
        </button>
      )}

      <p style={{ fontSize:'12px', color:'#999', textAlign:'center', marginTop:'8px' }}>
        Нажмите на фото 📷 чтобы изменить превью • Перетащите ☰ для сортировки
      </p>
    </div>
  );
}

// ─── Форма добавления блюда ────────────────────────────────────
function AddItemForm({ categories, onSave, onCancel, defaultCategoryId, enabledLanguages, priceCurrency = 'UZS' }) {
  const [form, setForm] = useState({
    name_ru:'', name_uz:'', name_en:'',
    price:'', weight:'',
    description_ru:'', description_uz:'', description_en:'',
    category_id: defaultCategoryId || categories[0]?.id || '',
    is_available: true,
  });

  const handleSubmit = () => {
    if (!form.name_ru.trim()) { alert('Введите название'); return; }
    if (!form.price || Number(form.price) <= 0) { alert('Введите цену'); return; }
    onSave(form);
  };

  return (
    <div style={{ ...styles.card, flexDirection:'column', gap:'10px',
      border:'2px solid #000', marginBottom:'16px' }}>
      <div style={{ fontWeight:'700', fontSize:'16px' }}>➕ Новое блюдо</div>
      <input style={styles.input} value={form.name_ru} autoFocus
        onChange={(e) => setForm({ ...form, name_ru: e.target.value })}
        placeholder="Название блюда *" />
      <input style={styles.input} type="number" value={form.price}
        onChange={(e) => setForm({ ...form, price: e.target.value })}
        placeholder={priceCurrency === 'RUB' ? 'Цена (₽) *' : 'Цена (сум) *'} />
      <input style={styles.input} value={form.weight}
        onChange={(e) => setForm({ ...form, weight: e.target.value })}
        placeholder="Вес / объём (250г)" />
      <textarea style={{ ...styles.input, height:'70px', resize:'none' }}
        value={form.description_ru}
        onChange={(e) => setForm({ ...form, description_ru: e.target.value })}
        placeholder="Описание (RU, необязательно)" />
      <TranslationsEditor item={form} onChange={setForm} enabledLanguages={enabledLanguages} />
      <select style={styles.input} value={form.category_id}
        onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
        <option value="">— Без категории —</option>
        {categories.map((cat) => (
          <option key={cat.id} value={cat.id}>{cat.name_ru || cat.name}</option>
        ))}
      </select>
      <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'14px' }}>
        <input type="checkbox" checked={form.is_available}
          onChange={(e) => setForm({ ...form, is_available: e.target.checked })} />
        Доступно в меню
      </label>
      <div style={{ display:'flex', gap:'8px' }}>
        <button style={{ ...styles.btnSave, flex:1 }} onClick={handleSubmit}>
          Сохранить блюдо
        </button>
        <button style={styles.btnCancel} onClick={onCancel}>Отмена</button>
      </div>
    </div>
  );
}

// ─── Форма редактирования блюда ────────────────────────────────
function EditItemForm({ item, categories, onChange, onSave, onCancel, enabledLanguages, priceCurrency = 'UZS' }) {
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', gap:'8px' }}>
      <input style={styles.input} value={item.name_ru || ''}
        onChange={(e) => onChange({ ...item, name_ru: e.target.value })}
        placeholder="Название (RU)" />
      <input style={styles.input} type="number" value={item.price || ''}
        onChange={(e) => onChange({ ...item, price: Number(e.target.value) })}
        placeholder={priceCurrency === 'RUB' ? 'Цена (₽)' : 'Цена (сум)'} />
      <input style={styles.input} value={item.weight || ''}
        onChange={(e) => onChange({ ...item, weight: e.target.value })}
        placeholder="Вес (250г)" />
      <textarea style={{ ...styles.input, height:'80px', resize:'none' }}
        value={item.description_ru || ''}
        onChange={(e) => onChange({ ...item, description_ru: e.target.value })}
        placeholder="Описание (RU)" />
      <TranslationsEditor item={item} onChange={onChange} enabledLanguages={enabledLanguages} />
      <select style={styles.input} value={item.category_id || ''}
        onChange={(e) => onChange({ ...item, category_id: e.target.value })}>
        {categories.map((cat) => (
          <option key={cat.id} value={cat.id}>{cat.name_ru || cat.name}</option>
        ))}
      </select>
      <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'14px' }}>
        <input type="checkbox" checked={item.is_available}
          onChange={(e) => onChange({ ...item, is_available: e.target.checked })} />
        Доступно
      </label>
      <div style={{ display:'flex', gap:'8px' }}>
        <button onClick={onSave}   style={styles.btnSave}>Сохранить</button>
        <button onClick={onCancel} style={styles.btnCancel}>Отмена</button>
      </div>
    </div>
  );
}

// ─── Форма ресторана ───────────────────────────────────────────
function RestaurantForm({ restaurant, onCoverUpload, onSave }) {
  const [form, setForm] = useState(() => ({ ...restaurant }));
  const [saveState, setSaveState] = useState('idle');
  const [saveError, setSaveError] = useState('');

  // Только когда строка ресторана в БД реально обновилась — иначе любой fetchAll()
  // (меню, обложка…) подменял объект restaurant и сбрасывал несохранённые сегмент/валюту.
  const restaurantSyncKey = restaurant
    ? `${restaurant.id}|${restaurant.updated_at ?? ''}`
    : '';
  useEffect(() => {
    if (!restaurant) return;
    const seg = restaurant.market_segment || 'central_asia';
    let cur = normalizeCurrency(restaurant.currency);
    if (seg === 'russia') cur = 'RUB';
    else if (seg === 'central_asia') cur = 'UZS';
    setForm({
      ...restaurant,
      market_segment: seg,
      currency: cur,
      vk_url: restaurant.vk_url || '',
      max_messenger_url: restaurant.max_messenger_url || '',
    });
  }, [restaurantSyncKey]); // eslint-disable-line react-hooks/exhaustive-deps -- намеренно не [restaurant]

  const onSegmentChange = (seg) => {
    setForm((prev) => ({
      ...prev,
      market_segment: seg,
      ...(seg === 'russia' ? { currency: 'RUB' } : seg === 'central_asia' ? { currency: 'UZS' } : {}),
    }));
  };

  const handleSaveClick = async () => {
    setSaveState('saving');
    setSaveError('');
    const payload = {
      name: form.name,
      description: form.description,
      address: form.address,
      phone: form.phone,
      wifi_password: form.wifi_password,
      instagram: form.instagram,
      telegram: form.telegram,
      whatsapp: form.whatsapp,
      vk_url: form.vk_url,
      max_messenger_url: form.max_messenger_url,
      currency: form.currency,
      market_segment: form.market_segment,
    };
    try {
      await onSave(payload);
      setSaveState('success');
      setTimeout(() => setSaveState('idle'), 2500);
    } catch (err) {
      if (err?.response?.status === 401) return;
      setSaveState('error');
      setSaveError(err?.response?.data?.error || err?.message || 'Ошибка сохранения');
    }
  };

  const saveBtnBg =
    saveState === 'saving' ? '#888' :
      saveState === 'success' ? '#34C759' :
        saveState === 'error' ? '#C62828' :
          '#000';

  return (
    <div style={{ ...styles.card, flexDirection:'column' }}>

      {/* Обложка */}
      <div style={{ marginBottom:'16px' }}>
        <div style={{ fontWeight:600, marginBottom:'8px' }}>Обложка ресторана:</div>
        {form.cover_url
          ? <img src={form.cover_url} alt="cover"
              style={{ width:'100%', height:'200px', objectFit:'cover',
                       borderRadius:'12px', marginBottom:'8px' }} />
          : <div style={{ ...styles.cardImagePlaceholder, width:'100%', height:'120px',
                          marginBottom:'8px' }}>Нет фото</div>
        }
        <label style={{ ...styles.btnEdit, cursor:'pointer', display:'inline-block' }}>
          📷 Загрузить обложку
          <input type="file" accept="image/*" style={{ display:'none' }}
            onChange={(e) => onCoverUpload(e.target.files[0])} />
        </label>
      </div>

      <div style={{ marginBottom:'16px' }}>
        <div style={{ fontSize:'13px', color:'#666', marginBottom:'4px' }}>Сегмент / тип рынка</div>
        <select
          style={styles.input}
          value={form.market_segment || 'central_asia'}
          onChange={(e) => onSegmentChange(e.target.value)}
        >
          {MARKET_SEGMENTS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <div style={{ fontSize:'12px', color:'#999', marginTop:'6px' }}>
          {(MARKET_SEGMENTS.find((x) => x.value === (form.market_segment || 'central_asia')) || MARKET_SEGMENTS[0]).hint}
        </div>
      </div>

      <div style={{ marginBottom:'16px' }}>
        <div style={{ fontSize:'13px', color:'#666', marginBottom:'4px' }}>Валюта цен в меню</div>
        <select
          style={styles.input}
          value={form.currency === 'RUB' ? 'RUB' : 'UZS'}
          onChange={(e) => setForm({ ...form, currency: e.target.value })}
        >
          <option value="UZS">UZS — сумы</option>
          <option value="RUB">RUB — ₽</option>
        </select>
      </div>

      {/* Основные поля */}
      {[
        { key:'name',        label:'Название',       placeholder:'Название ресторана' },
        { key:'description', label:'Описание',        placeholder:'Описание ресторана' },
        { key:'address',     label:'📍 Адрес',        placeholder:'ул. Амира Темура, 15' },
        { key:'phone',       label:'📞 Телефон',      placeholder:'+998 71 123 45 67' },
        { key:'wifi_password',label:'📶 Пароль Wi-Fi', placeholder:'MyWifi123' },
      ].map(({ key, label, placeholder }) => (
        <div key={key} style={{ marginBottom:'12px' }}>
          <div style={{ fontSize:'13px', color:'#666', marginBottom:'4px' }}>{label}</div>
          <input style={styles.input} value={form[key] || ''}
            onChange={(e) => setForm({ ...form, [key]: e.target.value })}
            placeholder={placeholder} />
        </div>
      ))}

      {/* Соцсети */}
      <div style={{ fontWeight:600, margin:'8px 0 12px',
                    paddingTop:'12px', borderTop:'1px solid #E0E0E0' }}>
        Соцсети
      </div>
      {[
        { key:'instagram', label:'📸 Instagram', placeholder:'@yourrestaurant' },
        { key:'telegram',  label:'✈️ Telegram',  placeholder:'@yourrestaurant' },
        { key:'whatsapp',  label:'💬 WhatsApp',  placeholder:'+998901234567' },
        { key:'vk_url', label:'🔵 VK', placeholder:'club123 или https://vk.com/...' },
        { key:'max_messenger_url', label:'💬 MAX', placeholder:'Ссылка или путь max.ru' },
      ].map(({ key, label, placeholder }) => (
        <div key={key} style={{ marginBottom:'12px' }}>
          <div style={{ fontSize:'13px', color:'#666', marginBottom:'4px' }}>{label}</div>
          <input style={styles.input} value={form[key] || ''}
            onChange={(e) => setForm({ ...form, [key]: e.target.value })}
            placeholder={placeholder} />
        </div>
      ))}

      {saveError && saveState === 'error' && (
        <div style={{
          backgroundColor: '#FFF0F0', color: '#B71C1C',
          padding: '10px 12px', borderRadius: 10, fontSize: 13, marginTop: 4,
        }}>
          ❌ {saveError}
        </div>
      )}

      <button
        type="button"
        onClick={handleSaveClick}
        disabled={saveState === 'saving'}
        style={{
          ...styles.btnSave,
          width: '100%',
          backgroundColor: saveBtnBg,
          transition: 'background-color 0.25s',
          cursor: saveState === 'saving' ? 'not-allowed' : 'pointer',
        }}
      >
        {saveState === 'saving' ? '⏳ Сохраняем…' :
          saveState === 'success' ? '✅ Сохранено' :
            saveState === 'error' ? '💾 Повторить сохранение' :
              '💾 Сохранить данные ресторана'}
      </button>
    </div>
  );
}

function bannerColor(kind, hasText) {
  if (!hasText) {
    return { backgroundColor: '#E3F2FD', color: '#0D47A1', border: '1px solid #BBDEFB' };
  }
  switch (kind) {
    case 'error':
      return { backgroundColor: '#FFEBEE', color: '#B71C1C', border: '1px solid #FFCDD2' };
    case 'warning':
      return { backgroundColor: '#FFF8E1', color: '#E65100', border: '1px solid #FFE0B2' };
    case 'info':
      return { backgroundColor: '#E3F2FD', color: '#0D47A1', border: '1px solid #BBDEFB' };
    default:
      return { backgroundColor: '#E8F5E9', color: '#1B5E20', border: '1px solid #C8E6C9' };
  }
}

// ─── Стили ────────────────────────────────────────────────────
const styles = {
  page:    { maxWidth:'800px', margin:'0 auto', padding:'16px 12px',
             fontFamily:'-apple-system, sans-serif', backgroundColor:'#f9f9f9', minHeight:'100vh' },
  title:   { fontSize:'22px', fontWeight:'800', marginBottom:'12px' },
  topBannerHost: {
    position: 'fixed',
    left: 0,
    right: 0,
    top: 0,
    zIndex: 10050,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '8px 12px 0',
    paddingTop: 'max(8px, env(safe-area-inset-top))',
    pointerEvents: 'none',
  },
  topBannerCard: {
    pointerEvents: 'auto',
    maxWidth: 480,
    width: '100%',
    padding: '10px 14px',
    borderRadius: 12,
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 1.4,
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
  },
  tabs:    { display:'flex', flexDirection:'column', gap:'8px', marginBottom:'20px' },
  tab:     { display:'flex', alignItems:'center', gap:'12px', padding:'14px 16px',
             borderRadius:'14px', border:'none', backgroundColor:'#EBEBEB',
             cursor:'pointer', width:'100%', textAlign:'left', fontFamily:'inherit' },
  tabActive: { backgroundColor:'#000', color:'#fff' },

  card:    { backgroundColor:'#fff', borderRadius:'16px', padding:'12px', marginBottom:'10px',
             display:'flex', flexDirection:'column', gap:'10px',
             boxShadow:'0 2px 8px rgba(0,0,0,0.06)', overflow:'hidden' },

  cardImageWrap:        { position:'relative', flexShrink:0, alignSelf:'flex-start' },
  cardImage:            { width:'100%', maxHeight:'180px', objectFit:'cover',
                          borderRadius:'10px', display:'block' },
  cardImagePlaceholder: { width:'72px', height:'72px', backgroundColor:'#F0F0F0',
                          borderRadius:'10px', display:'flex', alignItems:'center',
                          justifyContent:'center', fontSize:'28px' },
  uploadBtn:   { position:'absolute', bottom:'-8px', right:'-8px', width:'28px', height:'28px',
                 backgroundColor:'#000', borderRadius:'50%', display:'flex',
                 alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:'14px' },
  cardInfo:    { flex:1, minWidth:0 },
  cardName:    { fontSize:'15px', fontWeight:'600', marginBottom:'4px',
                 wordBreak:'break-word', whiteSpace:'normal' },
  cardPrice:   { fontSize:'16px', fontWeight:'700', marginBottom:'4px' },
  cardMeta:    { fontSize:'12px', color:'#888', marginBottom:'10px',
                 wordBreak:'break-word', whiteSpace:'normal' },
  cardActions: { display:'flex', gap:'8px', flexWrap:'wrap' },

  input:     { width:'100%', padding:'10px 12px', borderRadius:'10px', border:'1px solid #E0E0E0',
               fontSize:'14px', boxSizing:'border-box', outline:'none' },
  btnEdit:   { padding:'8px 14px', borderRadius:'8px', border:'none',
               backgroundColor:'#F0F0F0', cursor:'pointer', fontSize:'13px',
               whiteSpace:'nowrap' },
  btnDelete: { padding:'8px 14px', borderRadius:'8px', border:'none',
               backgroundColor:'#FFEBEE', color:'#E85D4A', cursor:'pointer',
               fontSize:'13px', whiteSpace:'nowrap' },
  btnSave:   { padding:'12px 20px', borderRadius:'10px', border:'none', backgroundColor:'#000',
               color:'#fff', cursor:'pointer', fontSize:'14px', fontWeight:'600',
               whiteSpace:'nowrap' },
  btnCancel: { padding:'12px 14px', borderRadius:'10px', border:'none',
               backgroundColor:'#F0F0F0', cursor:'pointer', fontSize:'14px' },
};